import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { useMemo, Suspense, useEffect } from "react";
import { lazyWithRetry as lazy } from "@/lib/lazyWithRetry";
import { AuthProvider, useAuth } from "@/hooks/useAuth";
import { FrontDeskProvider } from "@/contexts/FrontDeskContext";
import { LocaleProvider } from "@/contexts/LocaleContext";
import { SpainLandingRedirect } from "@/components/landing/SpainLandingRedirect";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { PWAUpdatePrompt } from "@/components/PWAUpdatePrompt";
import { PlatformAdminRoute } from "@/components/auth/PlatformAdminRoute";
import { ShopGate } from "@/components/auth/ShopGate";
import { SoloArtistRestriction, SoloProRestriction } from "@/components/auth/SoloArtistRestriction";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { Loader2 } from "lucide-react";
import { HelmetProvider } from "react-helmet-async";

// Lazy-loaded Pages
const Landing = lazy(() => import("./pages/Landing"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Appointments = lazy(() => import("./pages/Appointments"));
const Consultations = lazy(() => import("./pages/Consultations"));
const ConsultationRequest = lazy(() => import("./pages/ConsultationRequest"));
const ClientCheckIn = lazy(() => import("./pages/ClientCheckIn"));
const WalkInCheckIn = lazy(() => import("./pages/WalkInCheckIn"));
const Clients = lazy(() => import("./pages/Clients"));
const Artists = lazy(() => import("./pages/Artists"));
const Piercers = lazy(() => import("./pages/Piercers"));
const ConsentForms = lazy(() => import("./pages/ConsentForms"));
const Settings = lazy(() => import("./pages/Settings"));
const Team = lazy(() => import("./pages/Team"));
const AuditLogs = lazy(() => import("./pages/AuditLogs"));
const Apprenticeships = lazy(() => import("./pages/Apprenticeships"));
const Finance = lazy(() => import("./pages/Finance"));
const Events = lazy(() => import("./pages/Events"));
const FlashEventSignup = lazy(() => import("./pages/FlashEventSignup"));
const PublicBooking = lazy(() => import("./pages/PublicBooking"));
const Auth = lazy(() => import("./pages/Auth"));
const CreateShop = lazy(() => import("./pages/CreateShop"));
const Demo = lazy(() => import("./pages/Demo"));
const NotFound = lazy(() => import("./pages/NotFound"));
const AdminProvision = lazy(() => import("./pages/AdminProvision"));
const AdminAnalytics = lazy(() => import("./pages/AdminAnalytics"));
const AdminTrialDropoffs = lazy(() => import("./pages/AdminTrialDropoffs"));
const Conversations = lazy(() => import("./pages/Conversations"));
const PaymentsGuide = lazy(() => import("./pages/PaymentsGuide"));
const POS = lazy(() => import("./pages/POS"));

const ShopChat = lazy(() => import("./pages/ShopChat"));
const GiftCards = lazy(() => import("./pages/GiftCards"));
const Inventory = lazy(() => import("./pages/Inventory"));

const EmailCampaigns = lazy(() => import("./pages/EmailCampaigns"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const MessagingConsent = lazy(() => import("./pages/MessagingConsent"));
const ShopProfile = lazy(() => import("./pages/ShopProfile"));
const ShopDirectory = lazy(() => import("./pages/ShopDirectory"));
const ArtistPortfolio = lazy(() => import("./pages/ArtistPortfolio"));
const PublicTrip = lazy(() => import("./pages/PublicTrip"));
const Trips = lazy(() => import("./pages/Trips"));
const Waitlist = lazy(() => import("./pages/Waitlist"));
const SalesDemo = lazy(() => import("@/pages/SalesDemo"));

// SEO Landing Pages
const TattooAppointmentSoftware = lazy(() => import("./pages/landing/TattooAppointmentSoftware"));
const TattooConsentForms = lazy(() => import("./pages/landing/TattooConsentForms"));
const TattooShopPayroll = lazy(() => import("./pages/landing/TattooShopPayroll"));
const TattooApprenticeshipTracking = lazy(() => import("./pages/landing/TattooApprenticeshipTracking"));
const VagaroAlternative = lazy(() => import("./pages/landing/VagaroAlternative"));
const IPaidVagaro = lazy(() => import("./pages/landing/IPaidVagaro"));
const Blog = lazy(() => import("./pages/Blog"));
const Pricing = lazy(() => import("./pages/Pricing"));
const ClientImport = lazy(() => import("./pages/ClientImport"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const VenueAlternative = lazy(() => import("./pages/landing/VenueAlternative"));
const TattooStudioManagementSoftware = lazy(() => import("./pages/landing/TattooStudioManagementSoftware"));
const TattooStudioProAlternative = lazy(() => import("./pages/landing/TattooStudioProAlternative"));
const FreshaAlternative = lazy(() => import("./pages/landing/FreshaAlternative"));
const InkoruAlternative = lazy(() => import("./pages/landing/InkoruAlternative"));
const TattooStudioAssistantAlternative = lazy(() => import("./pages/landing/TattooStudioAssistantAlternative"));
const ConsultationOwnerLanding = lazy(() => import("./pages/landing/ConsultationOwnerLanding"));

// Spanish SEO Landing Pages
const LandingES = lazy(() => import("./pages/landing/es/LandingES"));
const SoftwareGestionEstudioTatuaje = lazy(() => import("./pages/landing/es/SoftwareGestionEstudioTatuaje"));
const SoftwareCitasTatuaje = lazy(() => import("./pages/landing/es/SoftwareCitasTatuaje"));
const ConsentimientoTatuaje = lazy(() => import("./pages/landing/es/ConsentimientoTatuaje"));
const NominasEstudioTatuaje = lazy(() => import("./pages/landing/es/NominasEstudioTatuaje"));
const SeguimientoAprendizTatuaje = lazy(() => import("./pages/landing/es/SeguimientoAprendizTatuaje"));
const AlternativaVagaro = lazy(() => import("./pages/landing/es/AlternativaVagaro"));
const AlternativaFreshaES = lazy(() => import("./pages/landing/es/AlternativaFresha"));
const AlternativaInkoru = lazy(() => import("./pages/landing/es/AlternativaInkoru"));
const AlternativaTattooStudioPro = lazy(() => import("./pages/landing/es/AlternativaTattooStudioPro"));
const AlternativaTattooStudioAssistant = lazy(() => import("./pages/landing/es/AlternativaTattooStudioAssistant"));
const AlternativaVenue = lazy(() => import("./pages/landing/es/AlternativaVenue"));
const FormularioConsultaEstudio = lazy(() => import("./pages/landing/es/FormularioConsultaEstudio"));

function PageLoader() {
  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <Loader2 className="w-8 h-8 animate-spin text-primary" />
    </div>
  );
}

/**
 * Watches the URL for password-recovery tokens on ANY route and redirects to /auth
 * while preserving the hash/query so the Auth page can pick up the flow.
 *
 * Needed because Supabase's recovery email redirects to the Site URL (root), not
 * to /auth — the user arrives at "/" with `#type=recovery&access_token=...` and
 * no code on the root route is looking for it.
 */
function RecoveryRedirector() {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (location.pathname === "/auth") return; // already there, Auth.tsx handles it

    const hash = window.location.hash || "";
    const search = window.location.search || "";
    const hasRecoveryHash = hash.includes("type=recovery");
    const urlSearch = new URLSearchParams(search);
    const hasRecoveryQuery = urlSearch.get("type") === "recovery"
      || (!!urlSearch.get("token_hash") && urlSearch.get("type") === "recovery")
      || (!!urlSearch.get("code") && urlSearch.get("type") === "recovery");

    if (hasRecoveryHash || hasRecoveryQuery) {
      // Preserve search + hash so Auth.tsx can detect and handle it.
      navigate({ pathname: "/auth", search, hash }, { replace: true });
    }
  }, [location.pathname]);

  return null;
}

// Catch-all that handles:
//   1. /@:slug → /artist/:slug (React Router v6 can't reliably match a literal
//      "@" right before a :param or splat, so we intercept here)
//   2. everything else → NotFound
function CatchAllOrAtSlug() {
  const { pathname } = useLocation();
  if (pathname.startsWith("/@")) {
    const slug = pathname.slice(2).split(/[\/?#]/)[0];
    if (slug) return <Navigate to={`/artist/${slug}`} replace />;
  }
  return <NotFound />;
}

function AppRoutes() {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // Plaid OAuth resume: when the user finishes logging in at an OAuth bank,
  // Plaid redirects them back to the registered redirect_uri (the app root)
  // with ?oauth_state_id=... — push them to /settings where the
  // BankConnectionsCard can pick up the resume and finish the connection.
  useEffect(() => {
    if (
      location.pathname === "/" &&
      new URLSearchParams(location.search).has("oauth_state_id")
    ) {
      navigate(`/settings${location.search}`, { replace: true });
    }
  }, [location.pathname, location.search, navigate]);

  return (
    <Routes>
      {/* Public landing page */}
      <Route path="/" element={user ? (
        <ProtectedRoute>
          <ShopGate>
            <Dashboard />
          </ShopGate>
        </ProtectedRoute>
      ) : <SpainLandingRedirect><Landing /></SpainLandingRedirect>} />

      {/* Auth */}
      <Route path="/auth" element={<Auth />} />
      <Route path="/demo" element={<Demo />} />
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms-of-service" element={<TermsOfService />} />
      <Route path="/messaging-consent" element={<MessagingConsent />} />

      {/* Public pages */}
      <Route path="/directory" element={<ShopDirectory />} />
      <Route path="/shop/:slug" element={<ShopProfile />} />
      <Route path="/artist/:slug" element={<ArtistPortfolio />} />
      <Route path="/trip/:slug" element={<PublicTrip />} />
      <Route path="/consultation-request/:shopId" element={<ConsultationRequest />} />
      <Route path="/client-check-in/:token" element={<ClientCheckIn />} />
      <Route path="/walk-in-check-in/:token" element={<WalkInCheckIn />} />
      <Route path="/flash-signup/:eventId" element={<FlashEventSignup />} />
      <Route path="/book/:shopId" element={<PublicBooking />} />
      <Route path="/sales-demo/*" element={<SalesDemo />} />

      {/* SEO Landing Pages */}
      <Route path="/tattoo-appointment-software" element={<TattooAppointmentSoftware />} />
      <Route path="/tattoo-consent-forms" element={<TattooConsentForms />} />
      <Route path="/tattoo-shop-payroll" element={<TattooShopPayroll />} />
      <Route path="/tattoo-apprenticeship-tracking" element={<TattooApprenticeshipTracking />} />
      <Route path="/vagaro-alternative" element={<VagaroAlternative />} />
      <Route path="/i-paid-vagaro" element={<IPaidVagaro />} />
      <Route path="/blog" element={<Blog />} />
      <Route path="/blog/:slug" element={<BlogPost />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/tattoo-studio-management-software" element={<TattooStudioManagementSoftware />} />
      <Route path="/tattoo-studio-pro-alternative" element={<TattooStudioProAlternative />} />
      <Route path="/fresha-alternative" element={<FreshaAlternative />} />
      <Route path="/inkoru-alternative" element={<InkoruAlternative />} />
      <Route path="/tattoo-studio-assistant-alternative" element={<TattooStudioAssistantAlternative />} />
      <Route path="/venue-alternative" element={<VenueAlternative />} />
      <Route path="/for-studio-owners" element={<ConsultationOwnerLanding />} />

      {/* Spanish SEO Landing Pages */}
      <Route path="/es" element={<LandingES />} />
      <Route path="/es/software-gestion-estudio-tatuaje" element={<SoftwareGestionEstudioTatuaje />} />
      <Route path="/es/software-citas-tatuaje" element={<SoftwareCitasTatuaje />} />
      <Route path="/es/consentimiento-tatuaje" element={<ConsentimientoTatuaje />} />
      <Route path="/es/nominas-estudio-tatuaje" element={<NominasEstudioTatuaje />} />
      <Route path="/es/seguimiento-aprendiz-tatuaje" element={<SeguimientoAprendizTatuaje />} />
      <Route path="/es/alternativa-vagaro" element={<AlternativaVagaro />} />
      <Route path="/es/alternativa-fresha" element={<AlternativaFreshaES />} />
      <Route path="/es/alternativa-inkoru" element={<AlternativaInkoru />} />
      <Route path="/es/alternativa-tattoo-studio-pro" element={<AlternativaTattooStudioPro />} />
      <Route path="/es/alternativa-tattoo-studio-assistant" element={<AlternativaTattooStudioAssistant />} />
      <Route path="/es/alternativa-venue" element={<AlternativaVenue />} />
      <Route path="/es/formulario-consulta-estudio" element={<FormularioConsultaEstudio />} />

      {/* Create shop (requires auth but no shop) */}
      <Route path="/create-shop" element={<ProtectedRoute><CreateShop /></ProtectedRoute>} />

      {/* All-role routes. requireCapability is now the canonical gate;
          allowedRoles is kept as legacy defense and for older routes
          that haven't migrated. */}
      <Route path="/appointments" element={
        <ProtectedRoute requireCapability="view_appointments">
          <ShopGate><Appointments /></ShopGate>
        </ProtectedRoute>
      } />
      <Route path="/consultations" element={
        <ProtectedRoute requireCapability="view_consultations">
          <ShopGate><Consultations /></ShopGate>
        </ProtectedRoute>
      } />
      <Route path="/clients/import" element={
        <ProtectedRoute requireCapability="view_clients" allowedRoles={["owner", "manager"]}>
          <ShopGate><ClientImport /></ShopGate>
        </ProtectedRoute>
      } />
      <Route path="/clients" element={
        <ProtectedRoute requireCapability="view_clients">
          <ShopGate><Clients /></ShopGate>
        </ProtectedRoute>
      } />
      <Route path="/consent-forms" element={
        <ProtectedRoute requireCapability="view_consent_forms">
          <ShopGate><ConsentForms /></ShopGate>
        </ProtectedRoute>
      } />
      <Route path="/settings" element={
        <ProtectedRoute requireCapability="view_settings">
          <ShopGate><Settings /></ShopGate>
        </ProtectedRoute>
      } />
      <Route path="/messages" element={
        <ProtectedRoute requireCapability="view_messages">
          <ShopGate><Conversations /></ShopGate>
        </ProtectedRoute>
      } />
      <Route path="/shop-chat" element={
        <ProtectedRoute requireCapability="view_shop_chat">
          <ShopGate><ShopChat /></ShopGate>
        </ProtectedRoute>
      } />
      <Route path="/pos" element={
        <ProtectedRoute requireCapability="view_pos">
          <ShopGate><SoloProRestriction feature="Point of Sale"><POS /></SoloProRestriction></ShopGate>
        </ProtectedRoute>
      } />

      {/* Owner/Manager only */}
      <Route path="/artists" element={
        <ProtectedRoute allowedRoles={["owner", "manager"]}>
          <ShopGate><SoloArtistRestriction restrictedFor="solo"><Artists /></SoloArtistRestriction></ShopGate>
        </ProtectedRoute>
      } />
      <Route path="/piercers" element={
        <ProtectedRoute allowedRoles={["owner", "manager"]}>
          <ShopGate><SoloArtistRestriction restrictedFor="solo"><Piercers /></SoloArtistRestriction></ShopGate>
        </ProtectedRoute>
      } />
      <Route path="/team" element={
        <ProtectedRoute allowedRoles={["owner", "manager"]}>
          <ShopGate><SoloArtistRestriction restrictedFor="solo"><Team /></SoloArtistRestriction></ShopGate>
        </ProtectedRoute>
      } />
      <Route path="/audit-logs" element={
        <ProtectedRoute requireCapability="view_audit_logs">
          <ShopGate><AuditLogs /></ShopGate>
        </ProtectedRoute>
      } />
      <Route path="/finance" element={
        <ProtectedRoute requireCapability="view_finance">
          <ShopGate><SoloProRestriction feature="Finance"><Finance /></SoloProRestriction></ShopGate>
        </ProtectedRoute>
      } />
      <Route path="/gift-cards" element={
        <ProtectedRoute requireCapability="view_gift_cards">
          <ShopGate><GiftCards /></ShopGate>
        </ProtectedRoute>
      } />
      <Route path="/inventory" element={
        <ProtectedRoute requireCapability="view_inventory">
          <ShopGate><Inventory /></ShopGate>
        </ProtectedRoute>
      } />
      <Route path="/products" element={<Navigate to="/inventory?tab=products" replace />} />
      <Route path="/email-campaigns" element={
        <ProtectedRoute requireCapability="view_email_campaigns">
          <ShopGate><EmailCampaigns /></ShopGate>
        </ProtectedRoute>
      } />
      <Route path="/add-ons" element={<Navigate to="/" replace />} />
      <Route path="/website-builder" element={<Navigate to="/" replace />} />
      <Route path="/payments-guide" element={
        <ProtectedRoute requireCapability="view_finance" allowedRoles={["owner", "manager"]}>
          <ShopGate><PaymentsGuide /></ShopGate>
        </ProtectedRoute>
      } />

      {/* Specific role combos */}
      <Route path="/apprenticeships" element={
        <ProtectedRoute allowedRoles={["owner", "manager", "apprentice"]}>
          <ShopGate><SoloArtistRestriction restrictedFor="solo"><Apprenticeships /></SoloArtistRestriction></ShopGate>
        </ProtectedRoute>
      } />
      {/* Events: owner/manager only per phase-1-role-based-ui-hiding.md §11 Q5.
          Artists at LTT-style shops don't run flash events; owner organizes them.
          Tightened from ["owner","manager","artist"] to close the deep-link loophole
          left by the Sidebar refactor (Events nav item already hidden from artist). */}
      <Route path="/events" element={
        <ProtectedRoute
          allowedRoles={["owner", "manager"]}
          requireCapability="view_events"
        >
          <ShopGate><SoloProRestriction feature="Events"><Events /></SoloProRestriction></ShopGate>
        </ProtectedRoute>
      } />
      <Route path="/trips" element={
        <ProtectedRoute requireCapability="view_visiting_artists">
          <ShopGate><Trips /></ShopGate>
        </ProtectedRoute>
      } />
      {/* Waitlist: owner/manager only. Tightened from ["owner","manager","artist","piercer"]
          because waitlist is a shop-level admin function. Closes the deep-link loophole
          for artists + piercers whose Sidebar entry was already hidden. */}
      <Route path="/waitlist" element={
        <ProtectedRoute
          allowedRoles={["owner", "manager"]}
          requireCapability="view_waitlist"
        >
          <ShopGate><Waitlist /></ShopGate>
        </ProtectedRoute>
      } />

      {/* Admin routes */}
      <Route path="/admin/analytics" element={<PlatformAdminRoute><AdminAnalytics /></PlatformAdminRoute>} />
      <Route path="/admin/provision" element={<PlatformAdminRoute><AdminProvision /></PlatformAdminRoute>} />
      <Route path="/admin/trial-dropoffs" element={<PlatformAdminRoute><AdminTrialDropoffs /></PlatformAdminRoute>} />

      {/* Catch-all */}
      <Route path="*" element={<CatchAllOrAtSlug />} />
    </Routes>
  );
}

const App = () => {
  const queryClient = useMemo(() => new QueryClient({
    defaultOptions: {
      queries: {
        retry: 1,
        staleTime: 1000 * 60 * 2,
        gcTime: 1000 * 60 * 10,
        refetchOnWindowFocus: false,
      },
    },
  }), []);

  return (
    <ErrorBoundary>
      <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <AuthProvider>
              <LocaleProvider>
                <FrontDeskProvider>
                  <RecoveryRedirector />
                  <Suspense fallback={<PageLoader />}>
                    <AppRoutes />
                  </Suspense>
                  <PWAUpdatePrompt />
                </FrontDeskProvider>
              </LocaleProvider>
            </AuthProvider>
          </BrowserRouter>
        </TooltipProvider>
      </QueryClientProvider>
      </HelmetProvider>
    </ErrorBoundary>
  );
};

export default App;
